import React from 'react';

import { connect } from 'react-redux';

import withLoyaltyAccess from '@/hocs/withLoyaltyAccess';
import { useTranslation } from '@/i18n';
import { Paginator, ServerStatus } from '@/models/globalModels';
import { BadgeItemType, RecognitionsPropsType, LoyaltyTier } from '@/models/loyaltyModels';
import { doGetLoyaltyBadges, doGetLoyaltyTiers } from '@/redux/loyalty/actions';
import { RootState } from '@/redux/rootReducer';

import BadgeItem from './BadgeItem';
import TierItem from './TierItem';

const mapDispatchToProps = {
  doGetLoyaltyBadges,
  doGetLoyaltyTiers,
};

const mapStateToProps = (state: RootState) => {
  const auth = state.auth;
  const loyalty = state.loyalty;
  return {
    ambassadorProfile: auth.ambassadorProfile,
    badgesStatus: loyalty.badgesStatus,
    badgesData: loyalty.badgesData,
    tiersStatus: loyalty.tiersStatus,
    tiersData: loyalty.tiersData,
  };
};

const checkIfBadgeEarned = (earnedBadges: BadgeItemType[], i: BadgeItemType) => {
  return earnedBadges.some((b) => b.id === i.id);
};

const checkIfTierEarned = (earnedBadges: LoyaltyTier[], i: LoyaltyTier) => {
  return earnedBadges.some((t) => t.id === i.id);
};

const sortByPoints = (tiers: LoyaltyTier[], pointsKey: keyof LoyaltyTier) => {
  return [...tiers].sort((a, b) => (Number(a[pointsKey]) ?? 0) - (Number(b[pointsKey]) ?? 0));
};

const LoyaltyRecognitions = ({
  ambassadorProfile,
  badgesStatus,
  badgesData,
  tiersStatus,
  tiersData,
  doGetLoyaltyBadges,
  doGetLoyaltyTiers,
}: RecognitionsPropsType) => {
  const { t } = useTranslation('dashboard');
  const [badgesList, setBadgesList] = React.useState<Paginator<BadgeItemType>>();
  const [tiersList, setTierList] = React.useState<Paginator<LoyaltyTier>>();
  const profileBadges = ambassadorProfile ? ambassadorProfile.badges : [];
  const profileTiers = ambassadorProfile ? ambassadorProfile.tiers : [];

  const sortedTier = React.useMemo(() => {
    if (!tiersList) return [];
    const earnedTiers = tiersList.results.filter((tier) => tier.is_active && checkIfTierEarned(profileTiers, tier));
    const notEarnedTiers = tiersList.results.filter((tier) => tier.is_active && !checkIfTierEarned(profileTiers, tier));
    return [...sortByPoints(earnedTiers, 'threshold'), ...sortByPoints(notEarnedTiers, 'threshold')];
  }, [tiersList]);

  const sortedBadges = React.useMemo(() => {
    if (!badgesList) return [];
    const earnedBadges = badgesList.results.filter((badge) => badge.is_active && checkIfBadgeEarned(profileBadges, badge));
    const notEarnedBadges = badgesList.results.filter((badge) => badge.is_active && !checkIfBadgeEarned(profileBadges, badge));
    return [...earnedBadges, ...notEarnedBadges];
  }, [badgesList]);

  React.useEffect(() => {
    if (badgesStatus === ServerStatus.FETCH) {
      setBadgesList(badgesData);
    }
  }, [badgesStatus, badgesData]);

  React.useEffect(() => {
    if (tiersStatus === ServerStatus.FETCH) {
      setTierList(tiersData);
    }
  }, [tiersStatus, tiersData]);

  React.useEffect(() => {
    doGetLoyaltyBadges();
  }, [doGetLoyaltyBadges]);

  React.useEffect(() => {
    doGetLoyaltyTiers();
  }, [doGetLoyaltyTiers]);

  return (
    <div className="Badges align-items-center justify-content-center">
      <div className="BadgesWrapper mt-3 f-raleway-regular">
        <h3 className="f-14 f-raleway-bold">{t('sidebar.tiersAndbadgesTitle')}</h3>
        <div className="badgeCollection p-0">
          {sortedTier.map((tier) => (
            <TierItem
              key={tier.id}
              color={tier.color}
              description={tier.description}
              earned={checkIfTierEarned(profileTiers, tier)}
              extraClass={tier.extraClass}
              image={tier.image}
              name={tier.name}
              points={tier.threshold || 0}
            />
          ))}
          <hr />
          {sortedBadges.map((badge) => (
            <BadgeItem
              key={badge.id}
              description={badge.description}
              earned={checkIfBadgeEarned(profileBadges, badge)}
              extraClass={badge.extraClass}
              image={badge.image}
              name={badge.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(withLoyaltyAccess(LoyaltyRecognitions));
